import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from './adminApi';
import { useFavicon } from '../utils/useFavicon';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #FAF8F5;
    color: #2C2C2C;
    -webkit-font-smoothing: antialiased;
  }
  .admin-login-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: radial-gradient(ellipse 80% 60% at 50% 30%, #FFF0E8 0%, #FAF8F5 70%);
  }
  .admin-login-logo {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 1.2rem;
    font-weight: 700;
    color: #2C2C2C;
    margin-bottom: 8px;
    letter-spacing: -0.02em;
  }
  .admin-login-logo img { height: 32px; width: auto; display: block; }
  .admin-login-logo span { color: #E8845A; }
  .admin-login-sub {
    font-size: 0.85rem;
    color: #888;
    margin-bottom: 36px;
  }
  .admin-login-card {
    background: #fff;
    border-radius: 20px;
    padding: 48px 44px;
    width: 100%;
    max-width: 400px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  }
  .admin-login-title {
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin-bottom: 28px;
    color: #1A1A1A;
  }
  .form-group { margin-bottom: 18px; }
  .form-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #444;
    margin-bottom: 7px;
  }
  .form-input {
    width: 100%;
    padding: 13px 16px;
    font-family: inherit;
    font-size: 1rem;
    border: 1.5px solid #E0DDD8;
    border-radius: 10px;
    background: #FDFCFB;
    color: #2C2C2C;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    -webkit-appearance: none;
  }
  .form-input:focus {
    border-color: #E8845A;
    box-shadow: 0 0 0 3px rgba(232,132,90,0.15);
  }
  .btn-submit {
    width: 100%;
    background: #E8845A;
    color: #fff;
    font-family: inherit;
    font-size: 1rem;
    font-weight: 600;
    padding: 15px;
    border-radius: 50px;
    border: none;
    cursor: pointer;
    margin-top: 8px;
    transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
  }
  .btn-submit:hover:not(:disabled) {
    transform: scale(1.02);
    background: #d9724a;
    box-shadow: 0 6px 20px rgba(232,132,90,0.4);
  }
  .btn-submit:disabled { opacity: 0.65; cursor: not-allowed; }
  .error-msg {
    background: #FFF0EE;
    border: 1px solid #F5C0B4;
    color: #C0392B;
    border-radius: 10px;
    padding: 12px 16px;
    font-size: 0.875rem;
    margin-bottom: 18px;
    line-height: 1.5;
  }
  @media (max-width: 480px) {
    .admin-login-card { padding: 36px 24px; }
  }
`;

export default function AdminLogin() {
  const navigate = useNavigate();
  // Admin surface — navy favicon. AdminLogin doesn't use AdminLayout
  // so it needs its own hook call.
  useFavicon('admin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await adminApi.login(email, password);
      localStorage.setItem('am_admin_token', data.token);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message === 'Invalid credentials'
        ? 'Invalid email or password.'
        : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <style>{styles}</style>
      <div className="admin-login-page">
        <div className="admin-login-logo">
          <img src="/logo/logo-mark-watercolor.png" alt="" aria-hidden="true" />
          Amigo Mio <span>Admin</span>
        </div>
        <div className="admin-login-sub">Internal dashboard — authorized access only</div>
        <div className="admin-login-card">
          <h1 className="admin-login-title">Sign in</h1>
          {error && <div className="error-msg">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className="form-input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                className="form-input"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>
            <button className="btn-submit" type="submit" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
